import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import SearchPage from './Pages/SearchPage/SearchPage';
import { EstimatorDashboardPropTypes } from 'src/interfaces/iproptypes';
import ReportPage from './Pages/ReportPage/ReportPage';
import { callApi } from 'src/core/callApi';
import { FullScreenAnimationLoader } from 'src/App/Components/Loaders';
import AutohideSnackbar from 'src/App/Components/Alerts/AutohideSnackbar';
import { convertApiCurrency } from './helper';
import { convertCurrency, convertToUSD, deepCopyObject } from 'src/utils/functions';
import { Alert, Box, Button, Divider, Grid, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { reportPageStyles as styles } from './Pages/ReportPage/styles';
import logo from 'src/App/assets/logoMobile.jpg';
import { User } from 'src/interfaces/iuser';
import { useDispatch } from 'react-redux';
import { deductEstimatorCreditAction } from 'src/store/Dashboard/actions';
import { geocodeAddress } from 'src/utils/functions';
import { COLOR_PRIMARY_2 } from 'src/colors';

const getCreateReportBodyFromHistoryState = async estimatorHistoryState => {
  const address_full = estimatorHistoryState.address_full;

  let latitude;
  let longitude;
  const coord = await geocodeAddress(address_full, true);
  if (coord && Array.isArray(coord) && coord[0] !== 0 && coord[1] !== 0) {
    latitude = coord[0];
    longitude = coord[1];
  }

  const address_short = estimatorHistoryState.address_short
    ? estimatorHistoryState.address_short
    : '';
  const bedrooms = estimatorHistoryState.bedrooms ? parseFloat(estimatorHistoryState.bedrooms) : 2;
  const report_type = estimatorHistoryState.report_type
    ? estimatorHistoryState.report_type
    : 'manage';
  const property_price = estimatorHistoryState.property_price
    ? parseFloat(estimatorHistoryState.property_price)
    : null;
  const monthly_rent = estimatorHistoryState.monthly_rent
    ? parseFloat(estimatorHistoryState.monthly_rent)
    : null;
  let currentReportType = report_type;

  const createRequestObj: { [key: string]: string | number | null } = {
    address_full,
    address_short,
    latitude,
    longitude,
    bedrooms,
    report_type,
    property_price,
    monthly_rent,
  };

  if (
    report_type === 'buy' &&
    (!createRequestObj.property_price || createRequestObj.property_price == 0)
  ) {
    currentReportType = 'manage';
  }

  if (
    report_type === 'rent' &&
    (!createRequestObj.monthly_rent || createRequestObj.monthly_rent == 0)
  ) {
    currentReportType = 'manage';
  }

  if (currentReportType === 'manage') {
    delete createRequestObj.property_price;
    delete createRequestObj.monthly_rent;
    createRequestObj.report_type = 'manage';
  }

  if (currentReportType === 'buy') {
    delete createRequestObj.monthly_rent;
  }

  if (currentReportType === 'rent') {
    delete createRequestObj.property_price;
  }
  return createRequestObj;
};

const ReportReadyModal = ({
  open,
  setOpen,
  setLoginModalOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLoginModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const handleCTAClick = () => {
    setOpen(false);
    setLoginModalOpen(true);
  };

  return (
    <div>
      <Modal
        open={open}
        aria-labelledby="share-link-modal"
        aria-describedby="modal-to-share-link"
        sx={{
          backdropFilter: 'blur(5px)', // Increase the blur here
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional: Add a semi-transparent background
        }}
      >
        <Box sx={{ ...styles.filterModalBodyRoot, width: 'clamp(300px, 70vw, 400px)' }}>
          <img src={logo} style={{ width: 130, height: 40, mixBlendMode: 'multiply' }} />
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="h6" component="h2">
              Your free report is ready! 🎉
            </Typography>
          </Box>

          <Divider />

          <Grid container xs justifyContent="center" sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Alert severity="warning"> Create a free Airbtics account to read this report</Alert>
            </Grid>
            <Grid item sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleCTAClick} sx={{ color: 'white' }}>
                Signup or Login
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
};

const ComingSoonModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <Modal open={open} aria-labelledby="share-link-modal" aria-describedby="modal-to-share-link">
        <Box sx={{ ...styles.filterModalBodyRoot, width: 'clamp(200px, 70vw, 300px)' }}>
          <IconButton onClick={handleClose} sx={styles.filterModalCloseIcon}>
            <CloseIcon />
          </IconButton>

          <Grid
            container
            item
            xs={12}
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="center"
            sx={{ mt: 1 }}
          >
            <Grid item>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLOR_PRIMARY_2 }}>
                Coming soon!
              </Typography>
            </Grid>
            <Grid item>
              <TimerOutlinedIcon sx={{ color: COLOR_PRIMARY_2 }} />
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
};
const handleCreditDeduction = (user: User, dispatch) => {
  const isUserHavingCredits: boolean = user.estimatorCredits > 0;
  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  if (isUserHavingCredits && !isUserSubscribed) {
    dispatch(deductEstimatorCreditAction(1));
  }
};

const EstimatorPage = ({
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  user,
  isLoggedIn,
  setLoginModalOpen,
  getUserInfo,
  matches,
  currency,
  isUserVerified,
  setIsEmailVerifyModalOpen,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  centerCoord,
  setCenterCoord,
  address,
  setAddress,
  dummyMarket,
  loginCountryCode,
  prevCenterCoord,
  setPrevCenterCoord,
  setEstimatorSimplePricingModalViewMode,
  dummyOverviewMarket
}: EstimatorDashboardPropTypes) => {
  const estimatorHistoryState = window.history.state;

  const pathname = window.location.pathname;
  const reportId = pathname.split('/')[3];
  const isReportPage = reportId !== '';

  const dispatch = useDispatch();

  const isUserHavingCredits: boolean = user.estimatorCredits > 0;
  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  const [reportData, setReportData] = useState<any>(null);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const [reportErrorMessage, setReportErrorMessage] = useState(null);
  const [reportUpdated, setReportUpdated] = useState(false);

  const [currencyHistory, setCurrencyHistory] = useState<string[]>([]);
  const [reportUpdateCount, setReportUpdateCount] = useState(0);
  const [reportReadyModalOpen, setReportReadyModalOpen] = useState(false);

  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);

  useEffect(() => {
    if (!reportId && reportData) {
      setReportData(_ => null);
    }     
  }, [reportId]);

  useEffect(() => {
    if (isLoggedIn && !user.isVerified) {
      setIsEmailVerifyModalOpen(true);
    }
  }, [user]);

  const handleCreateNewReport = () => {
    setReportData(null);
    window.history.pushState({}, '', '/airbnb-calculator');
  };

  const handleLoginOrPricingModalOpen = (
    state: 'open' | 'close',
    forceOpen: 'payment' | 'emailVerify' | null = null,
    estimatorSimplePricingModalViewMode: 'pro' | 'expert' | 'scale' = 'pro'
  ) => {
    setEstimatorSimplePricingModalViewMode(estimatorSimplePricingModalViewMode);

    if (user.email === 'Trial') {
      setLoginModalOpen(state === 'open' ? true : false);
      return;
    }
    
    if (forceOpen === 'payment') {
      setSubscriptionModalOpen(true);
      return;
    }

    if(forceOpen === 'emailVerify'){
      setIsEmailVerifyModalOpen(true);
      return;
    }    

    if (user.estimatorCredits > 0 || user.subscriptionPlan !== 'starter') {
      return;
    }

    setSubscriptionModalOpen(state === 'open' ? true : false);

    return;
  };

  const convertReportDataCurrency = (reportData: any, currency: string, prevCurrency: string) => {
    let convertedApiObject: any = null;
    if (prevCurrency === 'USD') {
      convertedApiObject = convertApiCurrency(deepCopyObject(reportData), currency);
    } else {
      const convertedApiObjectToUSD = convertApiCurrency(
        deepCopyObject(reportData),
        prevCurrency,
        true
      );
      convertedApiObject = convertApiCurrency(convertedApiObjectToUSD, currency);
    }
    return convertedApiObject;
  };

  const convertReportBodyIntoUSD = (reportData: any, currency: string) => {
    const convertedApiObject = convertApiCurrency(deepCopyObject(reportData), currency, true, true);
    return convertedApiObject;
  };

  const handleReport = async (method, requestBody: null | any, id?: string) => {
    setIsReportLoading(true);
    setReportErrorMessage(null);
    setReportUpdated(false);

    const url =
      'https://3iugob4wql.execute-api.us-east-2.amazonaws.com/default/airbnbCalculatorReportCRUD';
    let data;

    try {
      switch (method) {
        case 'CREATE':
          requestBody = convertReportBodyIntoUSD(requestBody, currency);
          data = await callApi(url, { body: requestBody }, true, false, '', 'POST', true);
          break;
        case 'READ':
          data = await callApi(url, { queryParams: { id } }, true, false, '', 'GET', true);
          break;
        case 'UPDATE':
          requestBody = convertReportBodyIntoUSD(requestBody, currency);
          data = await callApi(url, { body: requestBody }, true, false, '', 'PUT', true);
          break;
        default:
          console.error(`Unknown method: ${method}`);
          setIsReportLoading(false);
          return;
      }

      const isCurrencyNeedsToBeConverted = currency !== 'USD';

      if (data.error) {
        setReportErrorMessage(data.error);
      } else {
        setReportData(null);
        const { url_path } = data.primary_input;

        setReportData(_ =>
          isCurrencyNeedsToBeConverted ? convertApiCurrency(data, currency) : data
        );

        url_path && window.history.replaceState({}, '', `${window.location.origin}/${url_path}`);
        method == 'UPDATE' && setReportUpdated(true);
        method == 'CREATE' && handleCreditDeduction(user, dispatch);
      }
    } catch (error) {
      console.error('Error handling report:', error);
    } finally {
      setIsReportLoading(false);
    }
  };

  useEffect(() => {
    if (isUserHavingCredits || isUserSubscribed) {
      const createReportBody = localStorage.getItem('estimator-create-report');
      if (!createReportBody) {
        return;
      }
      handleReport('CREATE', JSON.parse(createReportBody));
      localStorage.removeItem('estimator-create-report');
    }
  }, [isUserHavingCredits, isUserSubscribed]);

  useEffect(() => {
    if (isReportPage && reportId) {
      handleReport('READ', null, reportId);
    }
  }, []);

  const getUpdatedCurrencyHistory = () => {
    const newCurrencyHistory = [...currencyHistory];
    newCurrencyHistory.push(currency);
    newCurrencyHistory.length > 2 && newCurrencyHistory.shift();
    return newCurrencyHistory;
  };

  //Maintains the currency history so I will know which currency it was previously
  useEffect(() => {
    const updatedCurrencyHistory = getUpdatedCurrencyHistory();
    setCurrencyHistory(_ => updatedCurrencyHistory);
  }, [currency]);

  useEffect(() => {
    if (!reportData) return;

    const prevCurrency = currencyHistory[0];
    const convertedApiObject: any = convertReportDataCurrency(reportData, currency, prevCurrency);

    setReportData(_ => convertedApiObject);
  }, [currencyHistory]);

  useEffect(() => {
    setReportUpdateCount(_ => reportUpdateCount + 1);
  }, [reportData]);

  useEffect(() => {
    const initializeReportCreation = async () => {
      if (window.google) {
        if (!estimatorHistoryState.address_full) {
          return;
        }

        const createReportBody = await getCreateReportBodyFromHistoryState(estimatorHistoryState);

        if (user.email === 'Trial') {
          localStorage.setItem('estimator-create-report', JSON.stringify(createReportBody));
          setReportReadyModalOpen(true);
        }
      }
    };
    initializeReportCreation();
  }, [window.google]);  

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Airbnb Profit Calculator: How much can I make on Airbnb?</title>
      </Helmet>

      {!reportData ? (
        <SearchPage
          user={user}
          matches={matches}
          currency={currency}
          dummyMarket={dummyMarket}
          googleAutocompleteSessionToken={googleAutocompleteSessionToken}
          getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
          clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
          googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
          handleReport={handleReport}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          setComingSoonModalOpen={setComingSoonModalOpen}
        />
      ) : (
        <ReportPage
          key={'reportPage-update-' + reportUpdateCount}
          reportData={reportData}
          currency={currency}
          handleCreateNewReport={handleCreateNewReport}
          handleReport={handleReport}
          matches={matches}
          user={user}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          setComingSoonModalOpen={setComingSoonModalOpen}
          dummyOverviewMarket={dummyOverviewMarket}
        />
      )}
      {isReportLoading && <FullScreenAnimationLoader />}

      {reportErrorMessage && <AutohideSnackbar message={reportErrorMessage} type="error" />}
      {reportUpdated && (
        <AutohideSnackbar message={'Report successfully updated!'} type="success" />
      )}
      {reportReadyModalOpen && (
        <ReportReadyModal
          open={reportReadyModalOpen}
          setOpen={setReportReadyModalOpen}
          setLoginModalOpen={setLoginModalOpen}
        />
      )}

      {comingSoonModalOpen && (
        <ComingSoonModal open={comingSoonModalOpen} setOpen={setComingSoonModalOpen} />
      )}
    </>
  );
};

export default EstimatorPage;
